import React from "react";
import SiteFooter from "@/components/SiteFooter";
import { PsyGestionIcon } from "@/components/PsyGestionLogo";

const C = {
  navy: "#1B3A5C",
  teal: "#2A9D8F",
  tealBg: "#E6F5F4",
  muted: "#6B85A3",
  border: "#D6E4F0",
  white: "#FFFFFF",
  bg: "#F7FAFE",
  green: "#16A34A",
  greenBg: "#DCFCE7",
};

interface SecurityCardProps {
  icon: string;
  title: string;
  description: React.ReactNode;
  badge?: string;
}

function SecurityCard({ icon, title, description, badge }: SecurityCardProps) {
  return (
    <div
      style={{
        backgroundColor: C.white,
        borderRadius: 16,
        border: `1px solid ${C.border}`,
        padding: "28px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 32 }}>{icon}</span>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>{title}</h3>
          {badge && (
            <span
              style={{
                display: "inline-block",
                marginTop: 4,
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 10px",
                borderRadius: 12,
                backgroundColor: C.greenBg,
                color: C.green,
              }}
            >
              {badge}
            </span>
          )}
        </div>
      </div>
      <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.8 }}>
        {description}
      </div>
    </div>
  );
}

export default function Securite() {
  return (
    <div
      style={{
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        backgroundColor: C.bg,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Nav */}
      <nav
        style={{
          backgroundColor: C.white,
          borderBottom: `1px solid ${C.border}`,
          padding: "16px 20px",
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            <PsyGestionIcon size={28} />
            <span style={{ fontWeight: 700, fontSize: 16, color: C.navy }}>PsyGestion</span>
          </a>
        </div>
      </nav>

      <main style={{ flex: 1, padding: "64px 20px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>

          {/* Hero */}
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <span style={{ fontSize: 48, display: "block", marginBottom: 16 }}>🔒</span>
            <h1
              style={{
                fontSize: "clamp(24px, 4vw, 36px)",
                fontWeight: 800,
                color: C.navy,
                marginBottom: 16,
                lineHeight: 1.2,
              }}
            >
              Vos données dans un coffre-fort numérique
            </h1>
            <p style={{ fontSize: 17, color: C.muted, maxWidth: 600, margin: "0 auto", lineHeight: 1.7 }}>
              En tant que psychologue, vous nous confiez des données sensibles liées à votre activité
              professionnelle. Voici exactement comment nous les protégeons — sans jargon technique inutile.
            </p>
          </div>

          {/* Trust badges */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 56,
            }}
          >
            {[
              { icon: "🇪🇺", label: "Hébergement Union Européenne" },
              { icon: "🔐", label: "Chiffrement AES-256" },
              { icon: "📋", label: "Conforme RGPD" },
              { icon: "🚫", label: "Zéro revente de données" },
            ].map((b) => (
              <div
                key={b.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 16px",
                  borderRadius: 20,
                  backgroundColor: C.tealBg,
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.teal,
                }}
              >
                <span>{b.icon}</span>
                <span>{b.label}</span>
              </div>
            ))}
          </div>

          {/* Grille des cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
              gap: 24,
              marginBottom: 56,
            }}
          >
            <SecurityCard
              icon="🏛️"
              title="Isolation des données (RLS)"
              badge="Protection niveau base de données"
              description={
                <>
                  <p style={{ margin: "0 0 8px" }}>
                    Chaque ligne de vos données (recettes, charges, profil) est verrouillée au niveau
                    de la base de données avec la technologie <strong>Row Level Security (RLS) de PostgreSQL</strong>.
                  </p>
                  <p style={{ margin: 0 }}>
                    Cela signifie que même si un attaquant obtenait un accès partiel à la base,
                    il lui serait <strong>techniquement impossible</strong> de lire vos données — elles ne
                    lui seraient tout simplement pas visibles. Chaque utilisateur ne voit que ce qui
                    lui appartient, garanti par votre identifiant unique (<code>auth.uid()</code>).
                  </p>
                </>
              }
            />

            <SecurityCard
              icon="🔐"
              title="Chiffrement AES-256"
              badge="Standard bancaire"
              description={
                <>
                  <p style={{ margin: "0 0 8px" }}>
                    Toutes vos données stockées sont chiffrées avec l'algorithme <strong>AES-256</strong>,
                    le même standard utilisé par les banques et les gouvernements.
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li><strong>Au repos</strong> : chiffrement au niveau du disque (PostgreSQL natif Supabase)</li>
                    <li><strong>En transit</strong> : HTTPS/TLS 1.3 entre votre navigateur et nos serveurs</li>
                    <li><strong>Mots de passe</strong> : jamais stockés — hachage bcrypt irréversible</li>
                  </ul>
                </>
              }
            />

            <SecurityCard
              icon="🧮"
              title="Algorithmes protégés côté serveur"
              badge="Propriété intellectuelle sécurisée"
              description={
                <>
                  <p style={{ margin: "0 0 8px" }}>
                    Nos formules de calcul fiscal (cotisations CIPAV, barème IR, comparatif Micro-BNC)
                    ne s'exécutent <strong>jamais dans votre navigateur</strong>.
                  </p>
                  <p style={{ margin: 0 }}>
                    Elles tournent dans des <strong>Edge Functions isolées</strong> (environnement Deno sécurisé),
                    accessibles uniquement aux utilisateurs authentifiés. Cela protège à la fois votre vie
                    privée (vos données ne transitent pas inutilement) et notre propriété intellectuelle.
                  </p>
                </>
              }
            />

            <SecurityCard
              icon="🚫"
              title="Zéro partage avec des tiers"
              badge="Promesse contractuelle"
              description={
                <>
                  <p style={{ margin: "0 0 8px" }}>
                    Vos données ne sont <strong>jamais vendues, partagées ni utilisées</strong> à des fins
                    publicitaires ou pour entraîner des modèles d'intelligence artificielle publics.
                  </p>
                  <p style={{ margin: 0 }}>
                    Nos seuls sous-traitants autorisés sont : <strong>Supabase</strong> (base de données, UE),
                    <strong> Stripe</strong> (paiements, UE) et <strong>Cloudflare</strong> (CDN) — tous liés
                    par des accords de traitement RGPD stricts.
                  </p>
                </>
              }
            />

            <SecurityCard
              icon="🇪🇺"
              title="Hébergement Union Européenne"
              badge="AWS Irlande (eu-west-1)"
              description={
                <>
                  <p style={{ margin: "0 0 8px" }}>
                    Vos données ne quittent pas le territoire de l'Union européenne.
                    L'infrastructure est hébergée sur <strong>AWS Irlande (eu-west-1)</strong>,
                    soumise au droit européen et au RGPD.
                  </p>
                  <p style={{ margin: 0 }}>
                    Les transferts vers des sous-traitants américains (Cloudflare) sont encadrés
                    par les <strong>Clauses Contractuelles Types (CCT)</strong> approuvées
                    par la Commission européenne.
                  </p>
                </>
              }
            />

            <SecurityCard
              icon="⏱️"
              title="Tokens JWT & Sessions sécurisées"
              badge="Expiration automatique"
              description={
                <>
                  <p style={{ margin: "0 0 8px" }}>
                    Votre session est gérée par des <strong>tokens JWT</strong> (JSON Web Tokens)
                    à expiration automatique (1 heure), renouvelés silencieusement tant que vous êtes actif.
                  </p>
                  <p style={{ margin: 0 }}>
                    En cas d'inactivité prolongée ou de changement de réseau, votre session expire
                    automatiquement. Aucune donnée d'accès n'est stockée côté serveur entre les sessions.
                  </p>
                </>
              }
            />
          </div>

          {/* Droits RGPD */}
          <div
            style={{
              backgroundColor: C.white,
              borderRadius: 16,
              border: `1px solid ${C.border}`,
              padding: "32px 28px",
              marginBottom: 40,
            }}
          >
            <h2 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 700, color: C.navy }}>
              📋 Vos droits RGPD — concrets et accessibles
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
              }}
            >
              {[
                { icon: "👁️", title: "Accès", desc: "Téléchargez toutes vos données en JSON depuis Mon Profil" },
                { icon: "✏️", title: "Rectification", desc: "Modifiez vos données à tout moment depuis votre profil" },
                { icon: "🗑️", title: "Effacement", desc: "Supprimez votre compte et toutes vos données en un clic" },
                { icon: "📤", title: "Portabilité", desc: "Export de vos données dans un format structuré et lisible" },
                { icon: "⛔", title: "Opposition", desc: "Contactez-nous pour vous opposer à un traitement" },
                { icon: "🏛️", title: "CNIL", desc: "En cas de litige non résolu, saisine possible de la CNIL" },
              ].map((r) => (
                <div
                  key={r.title}
                  style={{
                    padding: "14px 16px",
                    borderRadius: 10,
                    backgroundColor: C.bg,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <p style={{ margin: "0 0 4px", fontSize: 22 }}>{r.icon}</p>
                  <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 14, color: C.navy }}>{r.title}</p>
                  <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{r.desc}</p>
                </div>
              ))}
            </div>
            <p style={{ margin: "20px 0 0", fontSize: 13, color: C.muted }}>
              Pour exercer vos droits :{" "}
              <a href="mailto:contact@freedomarchitect.io" style={{ color: C.teal }}>
                contact@freedomarchitect.io
              </a>{" "}
              — Délai de réponse : 30 jours maximum.{" "}
              <a href="/confidentialite" style={{ color: C.teal }}>
                Politique de confidentialité complète →
              </a>
            </p>
          </div>

          {/* Question contact */}
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 15, color: C.muted, marginBottom: 12 }}>
              Une question sur la sécurité de vos données ?
            </p>
            <a
              href="mailto:contact@freedomarchitect.io"
              style={{
                display: "inline-block",
                padding: "12px 28px",
                borderRadius: 10,
                backgroundColor: C.teal,
                color: C.white,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
              }}
            >
              Contacter notre DPO →
            </a>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
